import type { Event, Log } from "@sentry/react";
import { sanitizeAnalyticsUrl } from "./analyticsUrl.ts";

const privateField =
  /(^|[_.-])(authorization|cookie|cookies|password|token|secret|email|birth_date|query|query_string|queryKey|queryHash|search|body|payload|variables|vars|arguments|reason|headers)([_.-]|$)/i;
const urlField = /(^|[_.-])(url|href|referrer|filename|abs_path|from|to|path)([_.-]|$)/i;
const safeUrl = (value: string) =>
  sanitizeAnalyticsUrl(value).replace(/(\/(?:i|invite|verify-email)\/)[^/?]+/gi, "$1redacted");
// The release name has an @ too. Keep only our own format, in its own field;
// any other email-shaped text is still scrubbed.
const buildRelease = /^stasht@\d+\.\d+\.\d+$/;

function safeText(value: string): string {
  return value
    .replace(/https?:\/\/[^\s"'<>]+/gi, safeUrl)
    .replace(
      /\b(GET|POST|PUT|PATCH|DELETE) (\/[^\s"'<>]+)/g,
      (_, method: string, path: string) => `${method} ${safeUrl(path)}`,
    )
    .replace(/\bBearer\s+[^\s,;]+/gi, "Bearer redacted")
    .replace(/[\w.%+-]+@[\w-]+(?:\.[\w-]+)+/g, "redacted")
    .replace(/\b(token|password|secret|query|search|q)\s*[:=]\s*[^\s,;]+/gi, "$1=redacted");
}

function safeData(value: unknown, depth = 0): unknown {
  if (typeof value === "string") return safeText(value);
  if (!value || typeof value !== "object") return value;
  if (depth > 12) return undefined;
  if (Array.isArray(value)) return value.map(item => safeData(item, depth + 1));
  return Object.fromEntries(
    Object.entries(value)
      .filter(([key]) => key !== "request" && key !== "response" && !privateField.test(key))
      .map(([key, item]) => [
        key,
        typeof item === "string" && urlField.test(key) ? safeUrl(item) : safeData(item, depth + 1),
      ]),
  );
}

/** Errors and traces keep exception names, stack locations and route context.
 * Request details, console text and query state can hold private input, and
 * none of it is needed to diagnose a failure. */
export function sanitizeTelemetryEvent<T extends Event>(event: T): T {
  const clean = safeData(event) as T;
  if (event.release && buildRelease.test(event.release)) {
    clean.release = event.release;
    if (event.tags?.release === event.release) clean.tags = { ...clean.tags, release: event.release };
  }
  if (event.user) clean.user = event.user.id ? { id: event.user.id } : {};
  if (event.request)
    clean.request = { url: event.request.url ? safeUrl(event.request.url) : undefined, method: event.request.method };
  if (event.logentry)
    clean.logentry = { message: event.logentry.message ? safeText(event.logentry.message) : undefined };
  // Console text can concatenate a response body with no field names to scrub.
  if (clean.breadcrumbs) clean.breadcrumbs = clean.breadcrumbs.filter(crumb => crumb.category !== "console");
  if (event.transaction)
    clean.transaction = event.transaction.startsWith("/") ? safeUrl(event.transaction) : safeText(event.transaction);
  return clean;
}

/** Console warnings and errors also go out as structured logs. */
export function sanitizeTelemetryLog(log: Log): Log {
  return {
    ...log,
    message: safeText(String(log.message)),
    ...(log.attributes ? { attributes: safeData(log.attributes) as Log["attributes"] } : {}),
  };
}

/** Session replay keeps its own console tab, and console text can hold a response body. */
export function dropConsoleFrames<T extends { data: { tag: string; payload: unknown } }>(frame: T): T | null {
  return frame.data.tag === "breadcrumb" && (frame.data.payload as { category?: string }).category === "console"
    ? null
    : frame;
}

const recordingText = (value: string) => (/^\/\S*$/.test(value) ? safeUrl(value) : safeText(value));
const join = (head: Uint8Array, body: Uint8Array) => {
  const out = new Uint8Array(head.length + body.length);
  out.set(head);
  out.set(body, head.length);
  return out;
};
const pipe = async (bytes: Uint8Array, through: CompressionStream | DecompressionStream) =>
  new Uint8Array(await new Response(new Blob([bytes.slice()]).stream().pipeThrough(through)).arrayBuffer());

/** A recording is a header line, then rrweb's events as JSON, zlib-compressed by the replay
 * worker or plain without it. The page snapshot records the address bar and link targets, which
 * no replay hook can rewrite, so every string value is scrubbed here and the JSON keeps its shape. */
export async function sanitizeReplayRecording(payload: string | Uint8Array): Promise<string | Uint8Array> {
  const bytes = typeof payload === "string" ? new TextEncoder().encode(payload) : payload;
  const split = bytes.indexOf(10) + 1;
  const head = bytes.subarray(0, split);
  const body = bytes.subarray(split);
  const zlib = body[0] === 0x78;
  const events = new TextDecoder().decode(zlib ? await pipe(body, new DecompressionStream("deflate")) : body);
  const clean = new TextEncoder().encode(
    JSON.stringify(JSON.parse(events, (_, value) => (typeof value === "string" ? recordingText(value) : value))),
  );
  if (!zlib) return typeof payload === "string" ? new TextDecoder().decode(join(head, clean)) : join(head, clean);
  return join(head, await pipe(clean, new CompressionStream("deflate")));
}

type EnvelopeItem = [{ type: string; length?: number }, unknown];

/** Replays go straight to the transport, past beforeSend, so they are scrubbed on the way out.
 * Every other envelope passes through as it is. */
export async function sanitizeTelemetryEnvelope<E>(envelope: E): Promise<E> {
  const [headers, items] = envelope as unknown as [unknown, EnvelopeItem[]];
  if (!items.some(([item]) => item.type.startsWith("replay_"))) return envelope;
  const clean = await Promise.all(
    items.map(async ([item, payload]): Promise<EnvelopeItem> => {
      if (item.type === "replay_event") return [item, sanitizeTelemetryEvent(payload as Event)];
      if (item.type !== "replay_recording") return [item, payload];
      const recording = await sanitizeReplayRecording(payload as string | Uint8Array);
      const length = typeof recording === "string" ? new TextEncoder().encode(recording).length : recording.length;
      return [{ ...item, length }, recording];
    }),
  );
  return [headers, clean] as unknown as E;
}
